import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { api } from "@/lib/api-client";
import { numberAndCountryCodeToFullPhoneNumber } from "@/Utilities";
import { cn } from "@/utils";
import { useDebouncedValue } from "@mantine/hooks";
import {
  useDisclosure,
  Button,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Input,
  Spinner,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
  RadioProps,
} from "@heroui/react";
import { Building, Plus, Search, User } from "lucide-react";
import { useEffect, useState } from "react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import {
  ManageCustomerModal,
  EntityType,
} from "@/Components/NextBase/Customer/ManageCustomerBasicInfoModal";
import { useForm } from "@mantine/form";

export const CustomerModal = ({
  isOpen,
  handleClose,
  preSelectedCustomerId,
  isPosAuthorized,
  types = ["physical", "legal"],
  confirmButtonProps,
}: {
  isOpen: boolean;
  handleClose: (data?: { customerId?: number; companyId?: number }) => void;
  preSelectedCustomerId?: number;
  isPosAuthorized?: boolean;
  types?: EntityType[];
  confirmButtonProps?: {
    text?: string;
  };
}) => {
  const {
    isOpen: isCreateCustomerModalOpen,
    onOpen: onOpenCreateCustomerModal,
    onClose: onCloseCreateCustomerModal,
  } = useDisclosure();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 200);

  const form = useForm<{
    selectedEntityId: number | null;
    selectedEntityType: EntityType;
  }>({
    initialValues: {
      selectedEntityId: null,
      selectedEntityType: types.includes("physical") ? "physical" : types[0],
    },
  });

  const { data: searchedCustomers, isFetching: isSearchingCustomers } =
    api.customer.useGetCustomers({
      searchQuery:
        form.getValues().selectedEntityType === "physical"
          ? debouncedSearchQuery
          : undefined,
      enabled: isPosAuthorized == null || isPosAuthorized === true,
    });

  const { data: searchedCompanies, isFetching: isSearchingCompanies } =
    api.company.useGetCompanies({
      searchQuery:
        form.getValues().selectedEntityType === "legal"
          ? debouncedSearchQuery
          : undefined,
      enabled: isPosAuthorized == null || isPosAuthorized === true,
    });

  useEffect(() => {
    if (isOpen) {
      if (preSelectedCustomerId) {
        form.setFieldValue("selectedEntityId", preSelectedCustomerId);
      } else {
        form.setFieldValue("selectedEntityId", null);
      }

      setSearchQuery("");
    }
  }, [isOpen]);

  const handleSubmit = (values: typeof form.values) => {
    if (values.selectedEntityType === "physical") {
      handleClose({
        customerId: values.selectedEntityId || undefined,
      });
    } else if (values.selectedEntityType === "legal") {
      handleClose({
        companyId: values.selectedEntityId || undefined,
      });
    }
  };

  return (
    <>
      <LimeModal
        size="xl"
        isOpen={isOpen}
        onClose={handleClose}
        classNames={{
          closeButton: "m-2",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();

                form.validate();

                if (form.isValid()) {
                  handleSubmit(form.getValues());
                }
              }}
            >
              <ModalHeader>
                <Trans>Stranka</Trans>
              </ModalHeader>
              <ModalBody className="px-0">
                <div className="flex gap-2 px-4">
                  <Input
                    endContent={
                      isSearchingCustomers || isSearchingCompanies ? (
                        <Spinner size="sm" />
                      ) : (
                        <></>
                      )
                    }
                    startContent={<Search />}
                    placeholder={t`Išči v imeniku`}
                    variant="bordered"
                    value={searchQuery}
                    autoFocus
                    onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  />
                  {types.length > 1 && (
                    <Tabs
                      aria-label={t`Izberite tip stranke`}
                      selectedKey={form.getValues().selectedEntityType}
                      onSelectionChange={(type) =>
                        form.setFieldValue(
                          "selectedEntityType",
                          type as EntityType,
                        )
                      }
                      disableAnimation
                    >
                      {types.includes("physical") && (
                        <Tab
                          key={"physical"}
                          data-identifier={"assign-customer-physical-tab"}
                          title={
                            <div className="flex items-center space-x-2">
                              <User />
                            </div>
                          }
                        />
                      )}
                      {types.includes("legal") && (
                        <Tab
                          key={"legal"}
                          data-identifier={"assign-customer-legal-tab"}
                          title={
                            <div className="flex items-center space-x-2">
                              <Building />
                            </div>
                          }
                        />
                      )}
                    </Tabs>
                  )}
                </div>

                <div className="max-h-[calc(100dvh-113px-60px-40px-60px-200px)] min-h-[50dvh] overflow-y-auto">
                  {form.getValues().selectedEntityType === "physical" &&
                    !searchedCustomers && (
                      <p className="text-foreground-400 mt-12 text-center">
                        <Trans>Iščite stranke</Trans>
                      </p>
                    )}
                  {form.getValues().selectedEntityType === "legal" &&
                    !searchedCompanies && (
                      <p className="text-foreground-400 mt-12 text-center">
                        <Trans>Iščite podjetja</Trans>
                      </p>
                    )}
                  {form.getValues().selectedEntityType === "physical" &&
                    !isSearchingCustomers &&
                    searchedCustomers &&
                    searchedCustomers.customers?.length === 0 && (
                      <p className="text-foreground-400 mt-12 text-center">
                        <Trans>Ni strank</Trans>
                      </p>
                    )}
                  {form.getValues().selectedEntityType === "legal" &&
                    !isSearchingCompanies &&
                    searchedCompanies &&
                    searchedCompanies.companies?.length === 0 && (
                      <p className="text-foreground-400 mt-12 text-center">
                        <Trans>Ni podjetij</Trans>
                      </p>
                    )}

                  <RadioGroup
                    classNames={{
                      wrapper: "gap-6 pl-4 pt-4",
                    }}
                    value={form.values.selectedEntityId?.toString() ?? null}
                    onValueChange={(value) =>
                      form.setFieldValue("selectedEntityId", Number(value))
                    }
                  >
                    {form.getValues().selectedEntityType === "legal" &&
                      searchedCompanies?.companies?.map((company, index) => {
                        return (
                          <CustomerCustomRadio
                            value={company.companyId.toString()}
                            index={index}
                            key={company.companyId}
                          >
                            <p className="font-semibold">
                              {company.companyName}
                            </p>
                            <p className="text-sm">{company.taxNumber}</p>
                            <p className="text-sm">
                              {company.address}, {company.zipCode}{" "}
                              {company.city}
                            </p>
                          </CustomerCustomRadio>
                        );
                      })}
                    {form.getValues().selectedEntityType === "physical" &&
                      searchedCustomers?.customers?.map((customer, index) => {
                        const customerGsm = (() => {
                          if (!customer.gsm || !customer.countryCode)
                            return undefined;

                          const parsedPhoneNumber =
                            numberAndCountryCodeToFullPhoneNumber(
                              customer.gsm,
                              customer.countryCode,
                            );
                          return parsedPhoneNumber;
                        })();

                        return (
                          <CustomerCustomRadio
                            value={customer.customerId.toString()}
                            index={index}
                            key={customer.customerId}
                          >
                            <p className="font-semibold">
                              {customer.name} {customer.lastName}
                            </p>
                            {customer.email && (
                              <p className="text-sm">{customer.email}</p>
                            )}
                            {customerGsm && (
                              <p className="text-sm">{customerGsm}</p>
                            )}
                          </CustomerCustomRadio>
                        );
                      })}
                  </RadioGroup>
                </div>
              </ModalBody>
              <ModalFooter className="flex-col p-2">
                <Button
                  variant="light"
                  className="h-6 w-fit py-4 pl-0"
                  size="sm"
                  onPress={onOpenCreateCustomerModal}
                  data-identifier="create-customer-button"
                >
                  <Plus className="bg-default h-6 w-6 rounded-full p-1" />
                  <p>
                    <Trans>Dodaj novo stranko</Trans>
                  </p>
                </Button>

                <Divider />

                <Button
                  type="submit"
                  className="h-12 w-full font-semibold"
                  isDisabled={!form.getValues().selectedEntityId}
                  onPress={() => {
                    if (form.getValues().selectedEntityType === "physical") {
                      handleClose({
                        customerId:
                          form.getValues().selectedEntityId || undefined,
                      });
                    } else if (
                      form.getValues().selectedEntityType === "legal"
                    ) {
                      handleClose({
                        companyId:
                          form.getValues().selectedEntityId || undefined,
                      });
                    }
                  }}
                >
                  {confirmButtonProps?.text || (
                    <Trans>Dodaj stranko na račun</Trans>
                  )}
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </LimeModal>

      <ManageCustomerModal
        isOpen={isCreateCustomerModalOpen}
        handleClose={(data: { customerId?: number; companyId?: number }) => {
          onCloseCreateCustomerModal();

          if (data.customerId || data.companyId) {
            handleClose(data);
          }
        }}
        defaultEntityType={form.getValues().selectedEntityType}
        showRedirectToCustomerCardButton={false}
      />
    </>
  );
};

const CustomerCustomRadio = (
  props: RadioProps & {
    index: number;
  },
) => {
  const { children, ...otherProps } = props;

  return (
    <Radio
      {...otherProps}
      classNames={{
        base: cn(
          "inline-flex bg-content1 hover:bg-content2 items-center justify-between",
          "flex-row-reverse w-full cursor-pointer gap-4 p-4  max-w-full rounded-xl bg-gray-50",
          "data-[selected=true]:outline-2 data-[selected=true]:outline-solid data-[selected=true]:outline-gray-300",
        ),
      }}
    >
      {children}
    </Radio>
  );
};
